/** One purchase document, its tax breakdown, payments and actions. */
import { useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRightLeft, Banknote, CheckCircle2, FileMinus, Pencil, Send, Trash2, Undo2 } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { amount, dec } from '../../lib/money';
import { shortDate } from '../../lib/format';
import { ReasonDialog } from '../../components/ReasonDialog';
import { Alert, Button, Card, Modal, Money, PageHeader, Skeleton, StatusChip, TableWrap, Td, Th } from '../../components/ui';
import { KIND_LABEL, STATUS_LABEL, STATUS_TONE, VAT_CLASS_LABEL, type PurchaseDetail, type PurchaseKind } from './types';

const NEXT: Partial<Record<PurchaseKind, PurchaseKind[]>> = {
  PURCHASE_REQUEST: ['PURCHASE_ORDER'],
  PURCHASE_ORDER: ['RECEIVING_REPORT', 'BILL'],
  RECEIVING_REPORT: ['BILL'],
};

export function PurchaseDetailPage(): JSX.Element {
  const { id } = useParams();
  const { can, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? {}) as { warnings?: string[]; error?: string };
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<'void' | 'reject' | 'discard' | null>(null);
  const [error, setError] = useState<string | null>(state.error ?? null);
  const doc = useQuery({ queryKey: ['purchase', id], queryFn: () => api<PurchaseDetail>(`/purchase-documents/${id}`) });
  const refresh = (): void => {
    setDialog(null);
    setError(null);
    void queryClient.invalidateQueries({ queryKey: ['purchases'] });
    void queryClient.invalidateQueries({ queryKey: ['purchase', id] });
  };
  const fail = (caught: unknown): void => setError(caught instanceof ApiError ? caught.message : 'That did not work.');
  const act = useMutation({ mutationFn: ({ path, body }: { path: string; body?: unknown }) => api<PurchaseDetail>(`/purchase-documents/${id}${path}`, { method: 'POST', body }), onSuccess: refresh, onError: fail });
  const discard = useMutation({ mutationFn: () => api(`/purchase-documents/${id}`, { method: 'DELETE' }), onSuccess: () => navigate(-1), onError: fail });
  const convert = useMutation({ mutationFn: (target: PurchaseKind) => api<PurchaseDetail>(`/purchase-documents/${id}/convert`, { method: 'POST', body: { target } }), onSuccess: (d) => navigate(`/purchases/${d.id}/edit`), onError: fail });

  if (doc.isLoading) return <Skeleton className="h-96 w-full" />;
  if (!doc.data) return <Alert tone="critical">Could not load this document.</Alert>;
  const d = doc.data;
  const posting = d.kind === 'BILL' || d.kind === 'SUPPLIER_CREDIT';
  const isMaker = d.createdById === user?.id;

  return (
    <>
      <PageHeader
        title={`${KIND_LABEL[d.kind]} ${d.number ?? '(draft)'}`}
        description={`${d.supplierView.registeredName} · ${shortDate(d.date)}${d.supplierInvoiceNo ? ` · their invoice ${d.supplierInvoiceNo}` : ''}`}
        actions={
          <>
            <StatusChip tone={STATUS_TONE[d.status]}>{STATUS_LABEL[d.status]}</StatusChip>
            {d.status === 'DRAFT' && can('bills.manage') && (
              <>
                <Link to={`/purchases/${d.id}/edit`}><Button variant="secondary"><Pencil aria-hidden="true" className="h-4 w-4" /> Edit</Button></Link>
                <Button variant="secondary" onClick={() => setDialog('discard')}><Trash2 aria-hidden="true" className="h-4 w-4" /> Discard</Button>
                <Button busy={act.isPending} onClick={() => act.mutate({ path: '/submit' })}><Send aria-hidden="true" className="h-4 w-4" /> {posting ? 'Post' : 'Approve'}</Button>
              </>
            )}
            {d.status === 'PENDING_APPROVAL' && can('bills.approve') && !isMaker && (
              <>
                <Button variant="secondary" onClick={() => setDialog('reject')}>Send back</Button>
                <Button busy={act.isPending} onClick={() => act.mutate({ path: '/approve' })}><CheckCircle2 aria-hidden="true" className="h-4 w-4" /> Approve</Button>
              </>
            )}
            {d.status === 'POSTED' && d.kind === 'BILL' && dec(d.balanceDue).gt(0) && can('payments.create') && (
              <Link to={`/payments/new?contact=${d.contactId}&bill=${d.id}`}><Button><Banknote aria-hidden="true" className="h-4 w-4" /> Pay</Button></Link>
            )}
            {d.status === 'POSTED' && d.kind === 'BILL' && can('bills.manage') && (
              <Link to={`/purchases/new/supplier_credit?contact=${d.contactId}&bill=${d.id}`}><Button variant="secondary"><FileMinus aria-hidden="true" className="h-4 w-4" /> Supplier credit</Button></Link>
            )}
            {d.status === 'APPROVED' && (NEXT[d.kind] ?? []).map((k) => (
              <Button key={k} variant="secondary" busy={convert.isPending} onClick={() => convert.mutate(k)}><ArrowRightLeft aria-hidden="true" className="h-4 w-4" /> To {KIND_LABEL[k].toLowerCase()}</Button>
            ))}
            {(d.status === 'POSTED' || d.status === 'APPROVED') && can('void.approve') && <Button variant="danger" onClick={() => setDialog('void')}><Undo2 aria-hidden="true" className="h-4 w-4" /> Void</Button>}
          </>
        }
      />
      {error && !dialog && <div className="mb-4"><Alert tone="critical">{error}</Alert></div>}
      {(state.warnings ?? []).map((w) => <div key={w} className="mb-4"><Alert tone="warning">{w}</Alert></div>)}
      {d.status === 'PENDING_APPROVAL' && isMaker && <div className="mb-4"><Alert tone="warning">Above your approval limit — an approver must post it.</Alert></div>}
      {d.status === 'VOIDED' && <div className="mb-4"><Alert tone="critical">Voided: {d.voidReason}</Alert></div>}
      <div className="grid gap-4 lg:grid-cols-[1fr_20rem]">
        <Card>
          <TableWrap>
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-800"><Th>Description</Th><Th align="right">Qty</Th><Th align="right">Net</Th><Th align="right">VAT</Th><Th>Class</Th><Th>ATC</Th><Th align="right">EWT</Th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {d.lines.map((l) => (
                <tr key={l.id}>
                  <Td>{l.description}</Td>
                  <Td align="right" className="tabular-nums">{Number(l.quantity)}</Td>
                  <Td align="right"><Money>{amount(l.netAmount)}</Money></Td>
                  <Td align="right"><Money>{amount(l.vatAmount, true)}</Money></Td>
                  <Td className="whitespace-nowrap">{dec(l.vatAmount).isZero() ? '—' : VAT_CLASS_LABEL[l.inputVatClass]}</Td>
                  <Td>{l.atc ?? '—'}</Td>
                  <Td align="right"><Money>{amount(l.ewtAmount, true)}</Money></Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
          <dl className="ml-auto mt-4 max-w-xs space-y-1 text-sm">
            <div className="flex justify-between"><dt className="text-slate-500">Net of VAT</dt><dd><Money>{amount(d.netAmount)}</Money></dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Input VAT</dt><dd><Money>{amount(d.inputVat)}</Money></dd></div>
            {!dec(d.nonCreditableVat).isZero() && <div className="flex justify-between"><dt className="text-slate-500">VAT kept in cost</dt><dd><Money>{amount(d.nonCreditableVat)}</Money></dd></div>}
            <div className="flex justify-between font-semibold"><dt>Total</dt><dd><Money>{amount(d.totalAmount)}</Money></dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">EWT {d.ewtAtBooking ? '(on booking)' : '(on payment)'}</dt><dd><Money>{amount(d.ewtAmount)}</Money></dd></div>
            {posting && <div className="flex justify-between border-t border-slate-200 pt-1 font-bold dark:border-slate-700"><dt>Owed to supplier</dt><dd><Money>{amount(d.payableAmount)}</Money></dd></div>}
            {d.kind === 'BILL' && d.status === 'POSTED' && <div className="flex justify-between font-semibold text-brand-700 dark:text-brand-100"><dt>Balance</dt><dd><Money>{amount(d.balanceDue)}</Money></dd></div>}
          </dl>
        </Card>
        <div className="space-y-4">
          <Card title="Supplier">
            <p className="text-sm font-semibold">{d.supplierView.registeredName}</p>
            <p className="text-sm text-slate-500">{d.supplierView.address || '—'}</p>
            <p className="text-sm tabular-nums">TIN {d.supplierView.tinFormatted ?? '—'}</p>
          </Card>
          {(d.payments.length > 0 || d.credits.length > 0) && (
            <Card title="Payments & credits">
              <ul className="space-y-2 text-sm">
                {d.payments.map((p) => <li key={p.paymentId} className="flex justify-between"><Link className="text-brand-700 hover:underline dark:text-brand-100" to={`/payments/${p.paymentId}`}>{p.number}</Link><span className={p.status === 'VOIDED' ? 'line-through opacity-60' : ''}><Money>{amount(p.amount)}</Money></span></li>)}
                {d.credits.map((c) => <li key={c.id} className="flex justify-between"><Link className="text-brand-700 hover:underline dark:text-brand-100" to={`/purchases/${c.id}`}>Credit {c.number ?? '(draft)'}</Link><Money>{amount(c.totalAmount)}</Money></li>)}
              </ul>
            </Card>
          )}
          <Card title="Record">
            <dl className="space-y-2 text-sm">
              {d.dueDate && <div><dt className="text-slate-500">Due</dt><dd>{shortDate(d.dueDate)}</dd></div>}
              {d.source && <div><dt className="text-slate-500">From</dt><dd><Link className="text-brand-700 hover:underline dark:text-brand-100" to={`/purchases/${d.source.id}`}>{KIND_LABEL[d.source.kind]} {d.source.number}</Link></dd></div>}
              {d.bill && <div><dt className="text-slate-500">Reduces</dt><dd><Link className="text-brand-700 hover:underline dark:text-brand-100" to={`/purchases/${d.bill.id}`}>{d.bill.number}</Link></dd></div>}
              {d.derived.map((x) => <div key={x.id}><dt className="text-slate-500">Carried into</dt><dd><Link className="text-brand-700 hover:underline dark:text-brand-100" to={`/purchases/${x.id}`}>{KIND_LABEL[x.kind]} {x.number ?? '(draft)'}</Link></dd></div>)}
              {d.journalEntryId && <div><dt className="text-slate-500">Ledger</dt><dd><Link className="text-brand-700 hover:underline dark:text-brand-100" to={`/journals/${d.journalEntryId}`}>View journal entry</Link></dd></div>}
            </dl>
          </Card>
        </div>
      </div>
      {dialog === 'void' && <ReasonDialog title={`Void ${d.number}?`} description="The ledger entry is reversed and the withholding record voided." confirmLabel="Void" withDate error={error} busy={act.isPending} onClose={() => { setDialog(null); setError(null); }} onConfirm={(reason, date) => act.mutate({ path: '/void', body: { reason, date } })} />}
      {dialog === 'reject' && <ReasonDialog title="Send back?" confirmLabel="Send back" tone="primary" error={error} busy={act.isPending} onClose={() => { setDialog(null); setError(null); }} onConfirm={(reason) => act.mutate({ path: '/reject', body: { reason } })} />}
      {dialog === 'discard' && (
        <Modal title="Discard this draft?" onClose={() => setDialog(null)}>
          <div className="flex justify-end gap-2"><Button variant="secondary" onClick={() => setDialog(null)}>Keep it</Button><Button variant="danger" busy={discard.isPending} onClick={() => discard.mutate()}>Discard</Button></div>
        </Modal>
      )}
    </>
  );
}
